//description: epoch line chart updater is widget component, used to update in real time
//     	       part of the components interface that requires to have
//             - isUpdateRealTime property
//             - update() method to update the component with new data
//			   - onDataStreamAdded() method to be notified with when data stream is added for the containing widget
//			   - isNotInterestedInAnyDataFields() return true of component is not interested in any datafields
//			   - resize() is repeatedly called when widget resizes

import ChartUtil from "./chart-util.js";
import InternalEvent from "../../../../core/constants/internalEvent.js";
import InternalEventBus from "../../../../core/utils/internalEventBus.js";
import DataStore from "../../../../core/stores/dataStore.js";
import WidgetService from "../../../../core/services/widgetService.js";
import WidgetType from "../../../../core/constants/widgetType.js";
import Widget from "../../../../core/models/widget";

var DataService = require('../../../../core/services/data-service/data-service.js');
var myUpdateFlagSetter = require('../my-update-flag-setter.js');
const nonContinuousChartTypes = ['bubble', 'bar', 'bar2'];

module.exports = function (widgetId, legends, metaData, isVertical, isAddOrEditWidget) {
  // //////// private members
  var _this = this;
  var chart = null;
  var divId = `${widgetId}-chart`;
  var lastKnownDatasetData = {};
  var initialData;
  var HISTORY_SIZE = metaData.historySize || 40;
  var snapshotLoadRegistrationTokens = {};

  var eventTokens = [];
  var timer;
  var resolutionDataMap = {};
  var historyMode = false;

  function init() {
    // generateEvaluationFunctions(legends);
    // legendsMap = createLegendsMap(legends);
    togglePreloader(true);
    initialData = createInitialData(legends);
    subscribeEvent(InternalEvent.Dashboard.CHART_LEGEND_CLICKED, toggleLayer);
    subscribeEvent(InternalEvent.Dashboard.CHART_LEGEND_HOVERED, toggleLayerFocus);
    subscribeEvent(InternalEvent.Dashboard.CHART_SLIDE, slideChart);
    subscribeEvent(InternalEvent.Dashboard.CHART_ZOOM, zoomChart);
    subscribeEvent(InternalEvent.Dashboard.UPDATE_BAR_CHART_TOOLTIPS, updateTooltip);
    subscribeEvent(InternalEvent.Dashboard.CHART_LEGEND_UPDATED, handleLegendUpdate);
    subscribeEvent(InternalEvent.Widget.HISTORY_MODE_TOGGLED, (payload) => {
      historyMode = payload.value;
      DataService.deregisterInterval(payload.widgetId);
    });
  }

  function subscribeEvent(event, handler) {
    let token = InternalEventBus.subscribe(event, handler);
    eventTokens.push(token);
  }

  function handleLegendUpdate(payload) {
    if (payload.widgetId === widgetId && chart && chart.forceUpdate) {
      chart.forceUpdate();
    }
  }

  function toggleLayer(data) {
    let shouldRaiseEvent = false;
    if (data.widgetId !== widgetId) return;

    if (data.legends.length > 0) {
      const label = data.legends[0].parameters.legendLabel;
      const legendId = data.legends[0].id;
      legends.forEach((l, index) => {
        if (l.id === legendId || (l.parameters.legendLabel.length > 0 && l.parameters.legendLabel === label)) {
          // find group legends
          legends.forEach((gl, i) => {
            if (l.id !== gl.id && l.parameters.groupId === gl.parameters.groupId) {
              gl.parameters.isActive = gl.parameters.isActive == null ? false : !gl.parameters.isActive;
              chart.toggleLayer('DataSeries' + i);
            }
          });
          l.parameters.isActive = l.parameters.isActive == null ? false : !l.parameters.isActive;
          chart.toggleLayer('DataSeries' + index);
          shouldRaiseEvent = true;
        }
      });
    }

    if (shouldRaiseEvent) {
      WidgetService.updateWidgetLegends(widgetId, legends);
      InternalEventBus.publish(InternalEvent.Dashboard.CHART_LEGEND_UPDATED, { widgetId });
    }
  }

  function toggleLayerFocus(data) {
    if (data.widgetId !== widgetId || chart == null) return;

    const focusedLayers = [];


    if (data.legends.length > 0) {
      const label = data.legends[0].parameters.legendLabel;
      const legendId = data.legends[0].id;

      legends.forEach(l => {
        if (l.id === legendId || (l.parameters.legendLabel.length > 0 && l.parameters.legendLabel === label)) {
          // find group legends
          legends.forEach(gl => {
            if (l.id !== gl.id && l.parameters.groupId === gl.parameters.groupId) {
              focusedLayers.push({
                id: gl.id
              });
            }
          });
          focusedLayers.push({
            id: l.id
          });
        }
      });
    }

    chart.toggleLayerFocus(focusedLayers);
  }


  function isLegendBubble(legend) {
    return legend.parameters.chartType === 'bubble';
  }

  function isLegendBar(legend) {
    return legend.parameters.chartType === 'bar' || legend.parameters.chartType === 'bar2';
  }

  function createInitialData(interestedLegends) {
    var allFields = [];
    for (var i in interestedLegends) {
      allFields.push("DataSeries" + i);
    }

    var iniData = [];
    for (var index in allFields) {
      var data = {};
      data.label = allFields[index];
      data.values = [];
      iniData.push(data);
    }
    return iniData;
  }

  /***
   * Requests snapshot data and prepares the chart data from the snapshot result.
   *
   * @param datasetRequests
   * @param numDataPoints
   * @param trimSide
   * @param updateInterval
   */
  function getSnapshotData(datasetRequests, numDataPoints, trimSide, updateInterval) {
    togglePreloader(true);
    // for server side functions with resolution
    if (isContainsServerSideFunction()) {
      const resolution = _this.getResolution();

      if (trimSide === 'left' || (resolution && resolution[0] === 'auto')) {
        togglePreloader(false);
        return Promise.resolve(null);
      }

      const options = datasetRequests.map(r => {
        return {
          fileBlockIndex: r.fileBlockIndex,
          fileBlockRecordIndex: r.fileBlockRecordIndex,
          numberOfDataPoints: numDataPoints,
          endTime: r.timestamp,
          startTime: r.timestamp
        }
      });
      const dataRequests = WidgetService.getFunctionRequestsForSnapshotData(legends, resolution, options);
      return DataService.getServerSideFunctionResult(dataRequests, resolution, numDataPoints)
        .then(response => {
          togglePreloader(false);
          _this.processingEventData(response.result.eventData);
          return processingSnapshotData(response.result.data, numDataPoints);
        })
    }

    if (_this.isResolutionMode()) {
      // datasetRequests doesn't have field id but snapshot data request for interval require field id
      const resolution = _this.getResolution();
      const baseTime = datasetRequests[0].timestamp;
      if (trimSide === 'left') {
        const newStartTime = baseTime - (resolution[1] * 1000 * numDataPoints);
        return _this.sendResolutionDataRequest(new Date(newStartTime), new Date(baseTime), numDataPoints);
      } else {
        let newEndTime = Math.min(Date.now(), baseTime + (resolution[1] * 1000 * numDataPoints));
        return _this.sendResolutionDataRequest(new Date(baseTime + resolution[1] * 1000), new Date(newEndTime), numDataPoints);
      }
    } else {
      return DataService.getSnapshotData(datasetRequests, numDataPoints, trimSide)
        .then(snapshotData => {
          togglePreloader(false);
          if (snapshotData) {
            return processingSnapshotData(snapshotData, numDataPoints);
          } else {
            return null;
          }
        });
    }
  }

  /***
   *
   * @param datasets
   * @param snapshotStartTime start time in seconds
   * @param numDataPoints
   * @returns {Promise.<TResult>}
   */
  function getSnapshotDataByTime(datasets, snapshotStartTime, numDataPoints, loadingBothSides) {
    if (isContainsServerSideFunction()) {
      return new Promise(resolve => {
        resolve(null)
      });
    } else if (_this.isResolutionMode()) {
      const resolution = _this.getResolution();
      const halfNumDataPoints = Math.round(numDataPoints / 2);
      const startTime = snapshotStartTime - (resolution[1] * 1000 * halfNumDataPoints);
      const endTime = snapshotStartTime + (resolution[1] * 1000 * halfNumDataPoints);
      return _this.sendResolutionDataRequest(new Date(startTime), new Date(endTime), numDataPoints);
    } else {
      snapshotStartTime = new Date(snapshotStartTime).toISOString();
      togglePreloader(true);
      return DataService.getSnapshotDataAfterAndBeforeTime(datasets, snapshotStartTime, numDataPoints)
        .then(result => {
          togglePreloader(false);
          return processingSnapshotData(result.data, result.numberOfData);
        });
    }
  }


  // resolution change callback
  this.getDataWithResolution = function (windowSize, endPoint) {
    const resolution = this.getResolution();
    let endTime = endPoint.getTime();
    const startTime = endTime - (resolution[1] * 1000 * windowSize);
    this.registerInterval();
    DataStore.emptyEvents(widgetId);

    if (resolution[4] === 'tick') {
      const widget = WidgetService.getWidget(widgetId);
      InternalEventBus.publish(InternalEvent.Dashboard.CHART_RESET, widget);
      return Promise.resolve();
    } else {
      return this.sendResolutionDataRequest(new Date(startTime), new Date(endTime), windowSize);
    }
  };

  this.getSnapshotDataWithResolution = function (direction, startTime, numDataPoints) {
    const resolution = this.getResolution();
    if (direction === 'left') {
      const newEndTime = new Date(startTime).getTime() - (resolution[1] * 1000);
      const newStartTime = newEndTime - (resolution[1] * 1000 * numDataPoints);
      return this.sendResolutionDataRequest(new Date(newStartTime), new Date(newEndTime), numDataPoints);
    } else {
      const newStartTime = new Date(startTime).getTime();
      let newEndTime = newStartTime + (resolution[1] * 1000 * numDataPoints);
      if (newEndTime > new Date().getTime()) {
        newEndTime = new Date().getTime()
      }
      return this.sendResolutionDataRequest(new Date(newStartTime), new Date(newEndTime), numDataPoints);
    }
  };


  this.sendTickDataRequest = function (endTime, windowSize) {
    const widget = WidgetService.getWidget(widgetId);
    const requestArgs = WidgetService.getRequestArguments(widget.legends, widgetId);
    togglePreloader(true);

    return DataService.getSnapshotDataBeforeTime(requestArgs.datasets.map(d => d.datasetId), endTime.toISOString(), windowSize)
      .then(result => {
        togglePreloader(false);
        return processingSnapshotData(result.data, result.numberOfData);
      });
  };

  this.sendResolutionDataRequest = function (startTime, endTime, windowSize) {
    const widget = WidgetService.getWidget(widgetId);
    const resolution = widget.metaData.resolution;
    const maxRepeatDuration = widget.metaData.maxRepeatDuration;
    const requestArgs = WidgetService.getRequestArguments(widget.legends, widgetId, { resolution });
    const eventRequests = requestArgs.events.map(e => {
      return DataService.getDataForTimeRange(e.datasetId, startTime.toISOString(), endTime.toISOString());
    });
    const requests = requestArgs.datasets.map(r => {
      return {
        name: 'TSCalculate',
        requestId: r.datasetId,
        params: [
          r.datasetId,
          r.fieldId,
          startTime.toISOString(),
          endTime.toISOString(),
          resolution[3],
          resolution[2],
          nonContinuousChartTypes.indexOf(r.chartType) > -1 ? 3 : 7,  // functionType
          null, // repeatTimeQuantity
          null, // repeatTimeUnit
          true, // includeRealtimeUpdate
          null, // businessDayOnly
          true  // emitEmptyWindow
        ]
      }
    });
    return Promise.all([
      Promise.all(eventRequests),
      DataService.getServerSideFunctionResult(requests, { startTime, endTime, resolution, maxRepeatDuration }, windowSize)
    ]).then(response => {
      togglePreloader(false);
      this.processingEventData(response[0]);
      return processingSnapshotData(response[1].result.data)
    });

  };

  this.getResolution = function () {
    const widget = WidgetService.getWidget(widgetId);
    return widget.metaData.resolution;
  };

  this.updateUserLastAction = function () {
    chart.updateUserLastAction();
  };

  function onChartSlide(direction, numberOfPoints) {
    InternalEventBus.publish(InternalEvent.Dashboard.CHART_SLIDE, {
      widgetId: widgetId,
      direction: direction,
      numberOfPoints: numberOfPoints
    });
  }

  function onChartZoom(windowSize) {
    InternalEventBus.publish(InternalEvent.Dashboard.CHART_ZOOM, {
      widgetId: widgetId,
      windowSize: windowSize
    });
    WidgetService.updateWidgetMetaData(widgetId, {
      historySize: windowSize
    });
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      InternalEventBus.publish(InternalEvent.Dashboard.CHART_LEGEND_UPDATED, { widgetId });
    }, 800);

  }

  function slideChart(eventPayload) {
    if (widgetId === eventPayload.widgetId) {
      chart.slide(eventPayload.direction, eventPayload.numberOfPoints);
    }
  }

  function zoomChart(eventPayload) {
    if (widgetId === eventPayload.widgetId) {
      chart.zoom(eventPayload.windowSize);
    }
  }

  function resumeRealTimeUpdate(fromWidgetId) {
    if (widgetId === fromWidgetId && chart) {
      chart._deActiveHistoryMode();
    }
  }

  function updateTooltip(data) {
    chart.drawDataPointDots(data.pointIndex, data.leftOffset, data.event, true);
  }

  function togglePreloader(show) {
    let preloader = $("#" + widgetId).find(".chart-preloader");

    if (show && !preloader.is(":visible")) {
      preloader.show();
    }

    if (!show) {
      preloader.hide();
    }
  }

  function isContainsServerSideFunction() {
    let contains = false;
    legends.forEach(legend => {
      if (legend.hasOwnProperty("serverSideFunction") && legend.serverSideFunction.length > 0) {
        contains = true;
      }
    });

    return contains;
  }

  /***
   * snapshotData {
   *    dataset1: [...],
   *    dataset2: [...],
   *    ..
   * }
   *
   *
   * @param snapshotData
   * @param noOfRecords
   * @returns {Array}
   */
  function processingSnapshotData(snapshotData, noOfRecords) {
    const maxRepeatDuration = parseInt(metaData.maxRepeatDuration);
    let data = [];
    let numberOfSnapshotDataRecords = 0;
    for (let prop in snapshotData) {
      if (snapshotData[prop].length > numberOfSnapshotDataRecords) {
        numberOfSnapshotDataRecords = snapshotData[prop].length;
      }
    }
    const datasetIds = Object.keys(snapshotData);
    legends.forEach((legend, legendIndex) => {
      data.push([]);
      let isLegendStale = false;

      for (let i = 0; i < numberOfSnapshotDataRecords; i++) {
        let datasetsData = {};
        let isStale = Object.keys(legend.mappings).length > 0;

        // find the current window's timestamp
        for (let j = 0; j < datasetIds.length; j++) {
          const d = snapshotData[datasetIds[j]][i];
          if (d && d.isMyDatasetUpdate) {
            datasetsData.time = d.epochSecond;
            break;
          }
        }

        /**
         * {
         *  dataset1: data,
         *  dataset2: data,
         *  dataset3: null,
         *  ...
         *  time: time,
         * }
         */
        for (let key in legend.mappings) {
          let datasetId = legend.mappings[key].datasetId;
          if (snapshotData.hasOwnProperty(datasetId)) {
            const d1 = snapshotData[datasetId][i];
            if (d1) {
              const timestampToCompare = (d1.originalTimestamp || d1.timestamp) + maxRepeatDuration * 60 * 1000;
              if (!d1.isStale && timestampToCompare >= datasetsData.time * 1000) {
                isStale = false;
              }
            }
            datasetsData[datasetId] = snapshotData[datasetId][i];
          } else {
            datasetsData[datasetId] = null;
          }
        }


        if (legend.hasOwnProperty("serverSideFunction")) {
          if (!Array.isArray(legend.serverSideFunction)) {
            legend.serverSideFunction = [legend.serverSideFunction];
          }
          legend.serverSideFunction.map(ssf => {
            const requestId = ssf.requestId;
            if (snapshotData.hasOwnProperty(requestId)) {
              datasetsData[requestId] = snapshotData[requestId][i];
              if (datasetsData[requestId] && datasetsData[requestId].isMyDatasetUpdate) {
                datasetsData.time = datasetsData[requestId].epochSecond;
              }
            }
          });
        }

        // if (datasetsData.time == null) {
        //   debugger;
        // }
        for (let key in legend.functions) {
          let yValue = null;
          try {
            yValue = legend.functions[key](datasetsData);
          } catch (e) {
          }
          datasetsData[key] = yValue;
        }

        if (isLegendBubble(legend)) {
          let isMyUpdate = false;

          for (let prop in datasetsData) {
            if (datasetsData[prop] && datasetsData[prop].isMyDatasetUpdate) {
              isMyUpdate = true;
              break;
            }
          }

          if (isMyUpdate === false || datasetsData.filter === false) {
            datasetsData.y = null;
          }
        }

        datasetsData.legendId = legend.id;

        if (isStale && maxRepeatDuration > 0) {
          datasetsData.y = null;
          isLegendStale = true;
        }

        // if the latest data is too old, legend is stale
        if (maxRepeatDuration > 0 && i === numberOfSnapshotDataRecords - 1) {
          if ((datasetsData.time + maxRepeatDuration) * 1000 < Date.now()) {
            isLegendStale = true;
          }
        }

        data[legendIndex].push(datasetsData);
      }


      if (isLegendStale && !legend.hasOwnProperty("serverSideFunction")) {
        InternalEventBus.publish('stale_data_check', { widgetId, legendId: legend.id, isStale: true });
      }
    });

    return data;
  }


  this.processingEventData = (eventData) => {
    if (!eventData) {
      return;
    }
    const eventDataMap = {};
    eventData.forEach(e => {
      const datasetId = e[0] ? e[0].dataset : null;
      if (datasetId) {
        eventDataMap[datasetId] = e;
      }
    });

    if (this.isResolutionMode()) {

      legends.forEach(l => {
        if (l.parameters.chartType === 'bubble') {

          let datasetId, isServerSideFunction = false, requestId;

          Object.keys(l.mappings).forEach(k => {
            datasetId = l.mappings[k].datasetId;
          });

          // server side function
          if (!datasetId) {
            l.serverSideFunction.forEach(sf => {
              datasetId = sf.params[0];
              requestId = sf.requestId;
              isServerSideFunction = true;
            });
          }

          if (eventDataMap[datasetId]) {
            const eventDataCopy = JSON.parse(JSON.stringify(eventDataMap[datasetId]));
            eventDataCopy.forEach(data => {
              const datasetsData = {};

              if (isServerSideFunction) {
                datasetsData[requestId] = data;
              } else {
                datasetsData[datasetId] = data;
              }

              for (let key in l.functions) {
                let yValue = null;
                try {
                  yValue = l.functions[key](datasetsData);
                } catch (e) {
                }
                data[key] = yValue;
              }
            });
            DataStore.storeEvents(`${widgetId}_${l.id}`, eventDataCopy);
          }
        }
      });
    }
  };

  // //////// public members
  this.id = `${widgetId}-controller`;
  this.widgetId = widgetId;
  this.historySize = HISTORY_SIZE;

  this.destroy = function () {
    DataService.deregisterInterval(widgetId);
    eventTokens.map(token => {
      InternalEventBus.unsubscribe(token);
    });
    legends.forEach(l => {
      DataStore.emptyEvents(`${widgetId}_${l.id}`)
    });
    eventTokens = [];
    if (chart) {
      chart.destroy();
      for (var registrationToken in snapshotLoadRegistrationTokens) {
        DataService.cancelSnapshotDataLoad(registrationToken);
      }
    }
  };

  this.isResolutionMode = function () {
    const widget = WidgetService.getWidget(widgetId);
    return widget.metaData.resolution && widget.metaData.resolution[0] !== 'tick';
  };

  this.update = function (updatingDatasetId) {
    // using resolution mode, we will update chart only when the window is elapsed.
    this._update(updatingDatasetId, this.isResolutionMode());
  };

  this._update = function (updatingDatasetId, preventPushingData = false) {
    if (!chart) {
      return;
    }
    const widget = WidgetService.getWidget(widgetId);
    const newEpochData = [];
    const maxTimestampByLegend = [];
    let maxTimestampForAllLegends = 0;
    let isCriticalUpdate = false;
    const metaData = widget.metaData;

    for (let i = 0; i < legends.length; ++i) {
      // prepare data for eval  function
      const datasetsData = {};
      maxTimestampByLegend.push(0);

      for (let key in legends[i].mappings) {
        const datasetId = legends[i].mappings[key].datasetId;
        const dataFromDataStore = DataStore.get(datasetId);
        datasetsData[datasetId] = dataFromDataStore || lastKnownDatasetData[datasetId];

        if (datasetsData[datasetId]) {
          const dataEpochSecond = datasetsData[datasetId].epochSecond;
          if (dataEpochSecond > maxTimestampForAllLegends) {
            maxTimestampForAllLegends = dataEpochSecond;
          }

          if (dataEpochSecond > maxTimestampByLegend[i]) {
            maxTimestampByLegend[i] = dataEpochSecond;
          }
        }

        // todo: investigate. why do we need this?
        // if ((isLegendBar(legends[i]) || isLegendBubble(legends[i])) && !preventPushingData) {
        //   const dataKey = datasetId + '_' + key;
        //   const storedDataTimestamp = dataFromDataStore ? dataFromDataStore.timestamp : null;
        //   if (resolutionDataMap[dataKey] && resolutionDataMap[dataKey].timestamp === storedDataTimestamp) {
        //     datasetsData[datasetId] = null;
        //   } else {
        //     resolutionDataMap[dataKey] = DataStore.get(datasetId);
        //   }
        // }
      }

      for (let key in legends[i].functions) {
        let yValue = null;
        try {
          yValue = legends[i].functions[key](datasetsData);
          if (key !== 'filter' && yValue.toString().indexOf('e') > -1) {
            yValue = 0;
          }
        } catch (e) {
        }

        datasetsData[key] = yValue;
      }

      if (isLegendBubble(legends[i])) {
        const isMyUpdate = myUpdateFlagSetter.setFlag(datasetsData, updatingDatasetId, legends[i].functions.filter);

        if (isMyUpdate === false || datasetsData.filter === false) {
          datasetsData.y = null;
        } else {
          isCriticalUpdate = true;
        }

        // bubble type on the resolution mode is the special case. we will handle data as an event data.
        if (preventPushingData && isMyUpdate) {
          let eventData = datasetsData;
          Object.keys(eventData[updatingDatasetId]).forEach(p => eventData[p] = eventData[updatingDatasetId][p]);
          DataStore.storeEvents(`${widget.id}_${legends[i].id}`, eventData);
        }
      }

      datasetsData.legendId = legends[i].id;
      newEpochData.push(datasetsData);
    }


    newEpochData.forEach((data, index) => {
      let isLegendStale = false;
      if (metaData.resolution) {
        const resolutionInMillis = metaData.resolution[1] * 1000;
        const currentTimeBucket = Math.floor(Date.now() / resolutionInMillis) * resolutionInMillis;

        // Stale data checks
        // Data should be null if the difference between the current time and data time is grater than maximum repeat time
        if (metaData.maxRepeatDuration > 0) {
          const timeDiff = currentTimeBucket - maxTimestampByLegend[index] * 1000;
          if (timeDiff > parseInt(metaData.maxRepeatDuration) * 1000 * 60) {
            data.y = null;
            isLegendStale = true;
          }
        }

        for (let prop in data) {
          if (typeof data[prop] === "object" && data[prop]) {
            data[prop].timestamp = currentTimeBucket;
            data[prop].epochSecond = Math.round(currentTimeBucket / 1000);
          }
        }
        data.time = Math.round(currentTimeBucket / 1000);
      } else {
        // assign max timestamp to all new data
        data.time = maxTimestampForAllLegends;

        // Stale data checks
        // constant value doesn't have timestamp
        if (metaData.maxRepeatDuration > 0 && maxTimestampByLegend[index] > 0) {
          const timeDiff = maxTimestampForAllLegends - maxTimestampByLegend[index] * 1000;
          if (timeDiff > parseInt(metaData.maxRepeatDuration) * 1000 * 60) {
            data.y = null;
            isLegendStale = true;
          }
        }
      }

      InternalEventBus.publish('stale_data_check', { widgetId, legendId: legends[index].id, isStale: isLegendStale });
    });


    if (!preventPushingData) {
      chart.push(newEpochData, isCriticalUpdate);
    }
  };

  // historicData = {
  //      datasetId: data[],
  //      datasetId: data[],
  //      ....
  // }
  this.loadHistoricData = function (historicData, noOfRecords, eventData, isRestored) {
    togglePreloader(false);
    const widget = WidgetService.getWidget(widgetId);
    var data = processingSnapshotData(historicData, noOfRecords);
    this.processingEventData(eventData);
    for (var i = 0; i < data.length; i++) {
      initialData[i].values = data[i];
    }

    // cache last known values from the historic data
    for (var datasetId in historicData) {
      var datasetHistoricData = historicData[datasetId];
      var lastIndex = datasetHistoricData.length - 1;
      lastKnownDatasetData[datasetId] = datasetHistoricData[lastIndex];
    }

    var container = $("#" + divId).parents("div.widget-container");


    // instantiate chart
    var $chart = $("#" + divId);
    chart = $chart.epoch({
      type: "time.series",
      data: initialData,
      axes: this.getRequiredAxesFromLegends(),
      margins: this.getRequiredMargins(),
      tickFormats: {
        left: tickFormatter,
        right: tickFormatter,
        bottom: time => {
          const widget = WidgetService.getWidget(widgetId);
          return Widget.getFormattedDateTimeString(widget.metaData, time * 1000);
        }
      },
      widgetMetaData: widget.metaData,
      historySize: HISTORY_SIZE,
      windowSize: HISTORY_SIZE,
      queueSize: 1,
      getSnapshotData: getSnapshotData,
      getSnapshotDataByTime: getSnapshotDataByTime,
      getDataWithResolution: (w, e) => this.getDataWithResolution(w, e),
      onChartSlide: onChartSlide,
      onChartZoom: onChartZoom,
      autoAdjustWindowSize: widget.autoAdjustWindowSize,
      centerZeroLine: widget.centerZeroLine,
      splitLeftRight: widget.splitLeftRight,
      isAddOrEditWidget: isAddOrEditWidget,
      updateInterval: metaData.updateInterval,
      getWidgetLegends: function () {
        let widget = WidgetService.getWidget(widgetId);
        return widget ? widget.legends : []
      },
      isRestored
    });


    if (container.length > 0) {
      if (container.parent().attr("id") === "single-widget-container") {
        ChartUtil.resizeCharts("single-widget-container", container.height(), container.width(), isVertical);
      } else {
        ChartUtil.resizeCharts(widgetId, container.height(), container.width(), isVertical);
      }
    }


    // set layer visibility
    for (var i = 0; i < legends.length; i++) {
      if (legends[i].parameters.isActive === false) chart.toggleLayer(i);
    }


    this.registerInterval();

    function tickFormatter(d) {
      var multiplier = Math.pow(10, 10);
      return (Math.round(d * multiplier) / multiplier);
    }
  };

  this.registerInterval = function () {
    const widget = WidgetService.getWidget(widgetId);
    const metaData = widget.metaData;
    const legendWithServerSideFunction = widget.legends.filter(l => l.serverSideFunction && l.serverSideFunction.length);
    DataService.deregisterInterval(widgetId);
    if (metaData.resolution && metaData.resolution[0] !== 'tick' && legendWithServerSideFunction.length === 0) {
      const timeLeftToNextWindow = metaData.resolution[1] - Date.now() / 1000 % metaData.resolution[1];
      setTimeout(() => {
        this._update(null, false, this.getWindowStartEndTime(Date.now()));
        DataService.registerInterval(widgetId, metaData.resolution[1] * 1000, elapsedTimestamp => {
          this._update(null, false, this.getWindowStartEndTime(elapsedTimestamp));
        });
      }, timeLeftToNextWindow * 1000);
    }
  };

  this.getWindowStartEndTime = function (time) {
    const widget = WidgetService.getWidget(widgetId);
    const resolutionInMilliseconds = widget.metaData.resolution[1] * 1000;
    const nextWindow = Math.ceil(time / resolutionInMilliseconds);
    return {
      start: (nextWindow - 1) * resolutionInMilliseconds,
      end: nextWindow * resolutionInMilliseconds
    }
  };


  this.getRequiredAxesFromLegends = function () {
    var axes = ["bottom"];
    for (var i = 0; i < legends.length; i++) {
      var axeSide = legends[i].parameters.yAxisSide;
      if (axes.indexOf(axeSide) === -1) axes.push(axeSide);
    }
    return axes;
  };


  this.getRequiredMargins = function () {

    const widget = WidgetService.getWidget(widgetId);
    if (widget.metaData.margins) {
      return widget.metaData.margins;
    } else {
      if (widget.type === WidgetType.TIME_SERIES) {
        return {
          top: 0,
          right: this.isSpecificYAxisRequired("right") ? 60 : 0,
          bottom: 25,
          left: this.isSpecificYAxisRequired("left") ? 60 : 0
        }
      }

      if (widget.type === WidgetType.TIME_SERIES_VERTICAL) {
        return {
          top: 0,
          right: 60,
          bottom: 70,
          left: 0
        }
      }
    }

    function getYAxisSide(index) {
      return legends[index].parameters.yAxisSide;
    }
  };

  this.round = function (number, precision) {
    var factor = Math.pow(10, precision);
    var tempNumber = number * factor;
    var roundedTempNumber = Math.round(tempNumber);
    return roundedTempNumber / factor;
  };

  this.isSpecificYAxisRequired = function (side) {
    var required = false;
    for (var i = 0; i < legends.length; i++) {
      var axeSide = legends[i].parameters.yAxisSide;
      if (axeSide === side) {
        required = true;
        break;
      }
    }
    return required;
  };


  this.getHistorySize = function () {
    return HISTORY_SIZE;
  };

  this.getLegends = function () {
    return legends;
  };

  this.isHistoryMode = function () {
    return historyMode;
  }

  init();
};


// WEBPACK FOOTER //
// ./src/components/dashboard/widgets/timeseries-chart/controller.js